import {
    SimplePresentationRunner,
    SimplePresentationRunnerController
} from "../../../core/winpresentation/SimplePresentationRunner";
import {gsap, TimelineLite, TimelineMax, TweenLite} from "gsap";
import {PearlHarbor} from "../../PearlHarbor";
import {PearlHarborSymbol, SYMBOL_NAME} from "../../reels/PearlHarborSymbol";
import {PearlHarborHelper} from "../../utils/PearlHarborHelper";
import {PearlHarborReelView} from "../../reels/PearlHarborReelView";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {PearlHarborGameConfig} from "../../PearlHarborGameConfig";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {ITargetArea} from "../../server/PearlHarborModel";
import {PearlHarborSoundManager} from "../../sound/PearlHarborSoundManager";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {PearlHarborWinConfigs} from "../../configs/PearlHarborWinConfigs";
import {ISymbol} from "../../../core/reel/IReelData";
import {PearlHarborInitialWinView} from "../../view/PearlHarborInitialWinView";
import Spine = PIXI.spine.Spine;

export class PearlHarborBombWinRunner implements SimplePresentationRunner {
    private _winEffects:Spine[];
    private _noWinEffects:Spine[];
    private _winNumberTls:TimelineLite[] = [];

    private _skipped:boolean;

    private skip(tl:TimelineLite):void {
        tl.timeScale(4);
        this._winNumberTls.forEach(numberTl => {
            if(numberTl && numberTl.isActive()) {
                numberTl.timeScale(4);
            }
        });
        this._skipped = true;
    }

    private collapseReels(controller:SimplePresentationRunnerController):void {
        const mainTL:TimelineMax = gsap.timeline();
        //Collapse main reels
        const subCollapseReelsTLs:TimelineLite[] = [];
        PearlHarborSoundManager.avalancheReelCounter = 0;
        for(let i:number = 0; i < 7; i++) {
            const collapseReelTL = PearlHarbor.allReelsView.mainReelModels[i].collapseReel();
            if(collapseReelTL.duration() > 0) {
                PearlHarborSoundManager.avalancheReelCounter++;
            }
            subCollapseReelsTLs.push(collapseReelTL);
        }

        mainTL.add(() => {
            if(PearlHarbor.foregroundElements.boostedBet) {
                PearlHarbor.foregroundElements.hideBoostAnimationOnAvalanche();
            }
        });

        mainTL.add(subCollapseReelsTLs);

        if(mainTL.duration() == 0) {
            mainTL.add(gsap.to(this, {duration : 0.001}));
        }

        mainTL.add(() => {
            PearlHarborReelView.landingSymbolArray.length = 0;
        });
        mainTL.add(() => {
            this._winNumberTls = [];
            controller.finish();
        });
    }

    public shouldRun():boolean {
        return PearlHarborHelper.isAvalanche(PearlHarbor.model.data.nextMode);
    }

    private getWildExplodeAnim(symbol:PearlHarborSymbol):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        const wildSpine:PIXI.spine.Spine = new PIXI.spine.Spine(PearlHarbor.assetLoader.getSpineAsset("x_bomb"));
        NolimitApplication.getLayerByName(PearlHarborGameConfig.LAYERS.OVERLAY)?.addChild(wildSpine);
        wildSpine.position.set((symbol.reelId + 0.5) * PearlHarborGameConfig.symbolWidth - 8, (symbol.reelTargetPosition + 3) * PearlHarborGameConfig.symbolHeight * 0.5 + 18);
        wildSpine.alpha = 0;
        wildSpine.name = "x_bomb";
        tl.add(() => PearlHarborSoundManager.playBombDropExplodeSound());
        const wildSpineTween:SpineTween = new SpineTween(wildSpine, 0, "explode");

        tl.add([() => {
            wildSpine.alpha = 1;
        }, wildSpineTween, TweenLite.to(symbol, 0.4, {alpha : 0})], 0);
        tl.add(() => {
            PearlHarborHelper.shakeScreen();
        }, wildSpineTween.duration() * 0.5);
        tl.add(() => {
            //console.log(symbol.name);
            symbol.isRemoved = true;
            PearlHarbor.allReelsView.mainReelModels[symbol.reelId].reelView.removeAvalancheSymbol(symbol);
            wildSpine.parent?.removeChild(wildSpine);
            if(wildSpine){
                wildSpine.destroy();
            }
        });
        return tl;
    }

    public getNonWinExplodeAnimation(symbol:PearlHarborSymbol):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        if(!symbol.visible) {
            return tl;
        }
        const symSize:string = symbol.isBig ? (symbol.symSize[0].toString() + "x" + symbol.symSize[1].toString()) : "";
        const ts:PIXI.spine.Spine = new PIXI.spine.Spine(PearlHarbor.assetLoader.getSpineAsset(`win_animations${symbol.isSplit ? "_h" : ""}${symSize}`));
        const yOffset:number = symbol.reelTargetPosition + 3.5 + (symbol.isBig ? -1 : 0) + (symbol.isSplit?-0.5:0),
            xOffset:number = symbol.reelId + (symbol.isBig ? 0 : 0.5);
        ts.position.set(xOffset * PearlHarborGameConfig.symbolWidth - 5, -5 + yOffset * PearlHarborGameConfig.symbolHeight * 0.5);
        this._noWinEffects.push(ts);
        const spineTween:SpineTween = new SpineTween(ts, 0, "destroyed", 0);
        tl.add([
            () => {
                NolimitApplication.getLayerByName(PearlHarborGameConfig.LAYERS.OVERLAY)?.addChild(ts);
            }, spineTween], 0);
        tl.add([
            () => {
                if(symbol.isBig) {
                    const startReel:number = Math.floor(symbol.topLeftSeed / 14);
                    for(let i:number = 0; i < symbol.symSize[0]; i++) {
                        const view:PearlHarborReelView = PearlHarbor.allReelsView.mainReelModels[i + startReel].reelView,
                            startRow:number = view.getSymbolOnReelIdByTopLeftSeed(symbol.topLeftSeed);

                        for(let j:number = 0; j < symbol.symSize[1]; j++) {
                            const bigSymPart:ISymbol = view.getAllSymbols()[startRow + j];
                            bigSymPart.isRemoved = true;
                            view.removeAvalancheSymbol(bigSymPart);
                        }
                    }
                } else {
                    symbol.isRemoved = true;
                    PearlHarbor.allReelsView.mainReelModels[symbol.reelId].reelView.removeAvalancheSymbol(symbol);
                }
                //PearlHarborReelView.playSymExplodeSound();
            }
        ], 0.5);

        return tl;
    }

    public getWinExplodeAnimation(symbol:PearlHarborSymbol):TimelineLite {
        const breakTl:TimelineLite = new TimelineLite();
        if(!symbol.visible) {
            return breakTl;
        }
        const symSize:string = symbol.isBig ? (symbol.symSize[0] + "x" + symbol.symSize[1]) : "";
        const winExplode:PIXI.spine.Spine = new PIXI.spine.Spine(PearlHarbor.assetLoader.getSpineAsset(`win_animations${symbol.isSplit ? "_h" : ""}${symSize}`));
        const yOffset:number = symbol.reelTargetPosition + 3.5 + (symbol.isBig ? -1 : 0) + (symbol.isSplit?-0.5:0),
            xOffset:number = symbol.reelId + (symbol.isBig ? 0 : 0.5);
        winExplode.name = "winExplode";
        winExplode.visible = false;
        NolimitApplication.getLayerByName(PearlHarborGameConfig.LAYERS.OVERLAY)?.addChild(winExplode);
        winExplode.position.set(xOffset * PearlHarborGameConfig.symbolWidth-5, yOffset * PearlHarborGameConfig.symbolHeight * 0.5-4);
        const winExplodeTween:SpineTween = new SpineTween(winExplode, 0, `${PearlHarborWinConfigs.getWinType()}_win`);
        this._winEffects.forEach((winEffect:Spine) => {
            breakTl.add(TweenLite.to(winEffect, 0.2, {alpha : 0}), 0);
            breakTl.add(() => {
                NolimitApplication.getLayerByName(PearlHarborGameConfig.LAYERS.OVERLAY)?.removeChild(winEffect);
            }, 0.2);
        });

        breakTl.add([
            () => {
                winExplode.visible = true;
            },
            winExplodeTween,
            TweenLite.to(symbol, 0.4, {alpha : 0})
        ], 0);
        breakTl.add(() => {
            if(symbol.isBig) {
                const startReel:number = Math.floor(symbol.topLeftSeed / 14);
                for(let i:number = 0; i < symbol.symSize[0]; i++) {
                    const view:PearlHarborReelView = PearlHarbor.allReelsView.mainReelModels[i + startReel].reelView,
                        startRow:number = view.getSymbolOnReelIdByTopLeftSeed(symbol.topLeftSeed);

                    for(let j:number = 0; j < symbol.symSize[1]; j++) {
                        const bigSymPart:ISymbol = view.getAllSymbols()[startRow + j];

                        bigSymPart.isRemoved = true;
                        view.removeAvalancheSymbol(bigSymPart);
                    }

                }
            } else {
                symbol.isRemoved = true;
                PearlHarbor.allReelsView.mainReelModels[symbol.reelId].reelView.removeAvalancheSymbol(symbol);
            }

            NolimitApplication.getLayerByName(PearlHarborGameConfig.LAYERS.OVERLAY)?.removeChild(winExplode);
            if(winExplode){
                winExplode.destroy();
            }
        });
        return breakTl;
    }

    public getWinEffectAnimation(symbol:PearlHarborSymbol):TimelineLite {
        const breakTl:TimelineLite = new TimelineLite();
        if(!symbol.visible) {
            return breakTl;
        }
        const winEffect:PIXI.spine.Spine = new PIXI.spine.Spine(PearlHarbor.assetLoader.getSpineAsset("winAttention"));
        this._winEffects.push(winEffect);

        winEffect.name = "WinEffect";
        const yOffset:number = symbol.reelTargetPosition + 3 + (symbol.isBig ? -1 : (symbol.isSplit ? -0.25 : 0)),
            xOffset:number = symbol.reelId + (symbol.isBig ? 0 : 0.5);
        winEffect.position.set(xOffset * PearlHarborGameConfig.symbolWidth - 6, yOffset * PearlHarborGameConfig.symbolHeight * 0.5+6+(symbol.isSplit ? 0 :15));

        const symSize:string = symbol.isBig ? (symbol.symSize[0] + "x" + symbol.symSize[1]) : "";
        const winEffectTween:SpineTween = new SpineTween(winEffect, 0, `winAttention${symbol.isSplit ? "_h" : ""}${symSize}`);
        breakTl.add([
            () => {
                NolimitApplication.getLayerByName(PearlHarborGameConfig.LAYERS.OVERLAY)?.addChild(winEffect);
            },
            winEffectTween
        ], 0);
        return breakTl;
    }

    public playBombAnim(wildSym:PearlHarborSymbol):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        const wildTL:TimelineLite = new TimelineLite();
        const multiplierShineAnim:TimelineLite = PearlHarbor.allReelsView.mainReelModels[wildSym.reelId].reelView.multiplierShineFlyAnimation(wildSym);
        const breakAnim:TimelineLite = this.getWildExplodeAnim(wildSym);
        wildTL.add(breakAnim, 0);
        wildTL.add(multiplierShineAnim, 0.6);
        tl.add(wildTL, 0);
        return tl;
    }

    public regularWinLogic():TimelineLite {
        this._winEffects = [];
        this._noWinEffects = [];
        if(PearlHarbor.model.gameData.betClusterWins.length < 1) {
            return new TimelineLite();
        }
        const mainTL:TimelineLite = new TimelineLite();
        for(let i:number = 0; i < PearlHarbor.model.allWinPositions.length; i++) {
            for(let j:number = 0; j < PearlHarbor.model.allWinPositions[i].length; j++) {
                if(PearlHarbor.model.allWinPositions[i][j]) {
                    let symbol:PearlHarborSymbol = <PearlHarborSymbol>PearlHarbor.allReelsView.mainReelModels[i].getSymbol(j);
                    mainTL.add(this.getWinEffectAnimation(symbol), 0)
                }
            }
        }

        for(let idx:number = 0; idx < PearlHarbor.model.gameData.betClusterWins.length; idx++) {
            mainTL.add(() => {
                this._winNumberTls.push(PearlHarbor.initialWinView.playWinNumbers(PearlHarbor.model.gameData.betClusterWins[idx], this._skipped));
            }, 0.7 + PearlHarborInitialWinView.DELAY_BETWEEN_COMBO_WINS * idx);
        }

        mainTL.add(() => {
            PearlHarborSoundManager.playHighlightWinSound();
        }, 0);

        return mainTL;
    }

    private getNonWinSymBreakTL():TimelineLite {
        const nonWinSymBreakTLs:TimelineLite = new TimelineLite(),
            winExplosionStartTime:number = 0.5;
        let hasExplosion:boolean = false;
        let hasBomb:boolean = false;
        PearlHarbor.model.explodePositions.forEach((reel:boolean[], reelId:number) => {
            reel.forEach((row:boolean, i:number) => {
                hasExplosion = true;
                let symbol:PearlHarborSymbol = <PearlHarborSymbol>PearlHarbor.allReelsView.mainReelModels[reelId].getSymbol(i);
                if(PearlHarbor.model.allWinPositions[reelId][i]) {
                    if(symbol.symbolName == SYMBOL_NAME.BOMB) {
                        hasBomb = true;
                    } else {
                        nonWinSymBreakTLs.add(this.getWinExplodeAnimation(symbol), winExplosionStartTime);
                    }
                } else if(row) {
                    if(symbol.symbolName == SYMBOL_NAME.BOMB) {
                        hasBomb = true;
                    } else {
                        nonWinSymBreakTLs.add(this.getNonWinExplodeAnimation(symbol), 0);
                    }
                }
            });
        });
        if(!hasBomb && hasExplosion) {
            nonWinSymBreakTLs.add(() => PearlHarborSoundManager.playWinExplodeSound(), winExplosionStartTime);
        }
        return nonWinSymBreakTLs;
    }

    public regularBombLogic():TimelineLite {
        const mainTL:TimelineLite = new TimelineLite(),
            border:number[] = [13, 0, 6, 0];
        const winHighlightTime:number = 10 * 0.033;
        PearlHarbor.model.xbombPos.forEach((wildPos:number[], i:number) => {
            let bomb:PearlHarborSymbol = <PearlHarborSymbol>PearlHarbor.allReelsView.mainReelModels[wildPos[0]].getSymbol(wildPos[1]);
            mainTL.add(this.playBombAnim(bomb), winHighlightTime);
        });
        const center:number = 48,
            borderData:number[] = PearlHarbor.model.gameData.barriers2Delete;
        border[0] = Math.min((center + Math.max(borderData[0], -6)) % 14, border[0]);
        border[1] = Math.max((center + Math.min(borderData[1], 7)) % 14, border[1]);
        border[2] = Math.min(Math.floor((center + borderData[2]) / 14), border[2]);
        border[3] = Math.max(Math.floor((center + borderData[3]) / 14), border[3]);
        mainTL.add(PearlHarbor.activeSymbolsBackground.updateActiveArea(border), winHighlightTime + 0.5);
        mainTL.add(this.getNonWinSymBreakTL(), winHighlightTime);

        if(mainTL.duration() == 0) {
            mainTL.add(gsap.to(this, {duration : 0.001}));
        }
        return mainTL;
    }

    private checkActiveArea():void {
        let border:number[] = [13, 0, 6, 0];
        const targetArea:ITargetArea = PearlHarbor.activeSymbolsBackground.targetArea,
            center:number = 48,
            borderData:number[] = PearlHarbor.model.barriers;
        border[0] = Math.min((center + borderData[0]) % 14, border[0]);
        border[1] = Math.max((center + borderData[1]) % 14, border[1]);
        border[2] = Math.min(Math.floor((center + borderData[2]) / 14), border[2]);
        border[3] = Math.max(Math.floor((center + borderData[3]) / 14), border[3]);


        if(border[0] != Math.max(targetArea.rowStart, 0)) {
            Logger.logDev(targetArea.rowStart + " not same area data as server: reelStart: " + border[0]);
        }
        if(border[1] != Math.min(targetArea.rowEnd, 13)) {
            Logger.logDev(targetArea.rowEnd + " not same area data as server: reelEnd: " + border[1]);
        }
        if(border[2] != Math.max(targetArea.reelStart, 0)) {
            Logger.logDev(targetArea.reelStart + " not same area data as server: reelStart: " + border[2]);
        }
        if(border[3] != Math.min(targetArea.reelEnd, 6)) {
            Logger.logDev(targetArea.reelEnd + " not same area data as server: reelEnd: " + border[3]);
        }
    }

    public start(controller:SimplePresentationRunnerController):void {
        this._skipped = false;
        const mainTL:TimelineLite = new TimelineLite();
        controller.setSkipCallback(() => this.skip(mainTL));
        mainTL.add(() => {
            PearlHarbor.model.reels.forEach((reel:string[], i) => {
                reel.forEach((sym:string, j) => {
                    let symbol:PearlHarborSymbol = <PearlHarborSymbol>PearlHarbor.allReelsView.mainReelModels[i].getSymbol(j);
                    symbol.checkMultiplier();
                });
            });
        });
        mainTL.add(this.regularWinLogic());
        mainTL.add(this.regularBombLogic());
        mainTL.add(() => {
            this.checkActiveArea();
            this._winEffects.forEach((winEffect:Spine) => {
                NolimitApplication.getLayerByName(PearlHarborGameConfig.LAYERS.OVERLAY)?.removeChild(winEffect);
                winEffect.destroy();
            });
            this._noWinEffects.forEach((noWinEffect:Spine) => {
                NolimitApplication.getLayerByName(PearlHarborGameConfig.LAYERS.OVERLAY)?.removeChild(noWinEffect);
                noWinEffect.destroy();
            });
        });
        mainTL.add(() => {
            this.collapseReels(controller)
        });
    }
}